import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { createSafeSearchClient } from "../utils/safeSearchClient";
import {
  InstantSearch,
  SearchBox,
  Hits,
  Pagination,
  Configure,
  useConnector,
} from "react-instantsearch-hooks-web";
import connectStats from "instantsearch.js/es/connectors/stats/connectStats";
import connectPagination from "instantsearch.js/es/connectors/pagination/connectPagination";
import "instantsearch.css/themes/reset.css";
import "instantsearch.css/themes/satellite.css";

import FiltersPanel from "./FiltersPanel";
import BoardsFiltersPanel from "./BoardsFiltersPanel";
import IndexSwitcher from "./IndexSwitcher";
import ConverterCardLite from "./ConverterCardLite";
import DetailsSheetLite from "./DetailsSheetLite";
import NoFavorites from "./NoFavorites";
import ListSelector from "./ListSelector";
import ManageListsModal from "./ManageListsModal";
import IDX from "../utils/indices";
import { useUser } from "../context/UserProvider";
import { useTheme } from "../context/ThemeContext";
import { ScreenGuard } from "./ScreenGuard";
import { addUserBuyListItem, addUserBuyListItemBoard } from "../services/buyLists";
import {
  getBaseUSD,
  getPriceForCurrency,
  applyLocalDiscount,
} from "../utils/priceHelpers";
import { ShieldAlert } from "lucide-react";

// Firebase (modular)
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../lib/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
const apiKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;

const MATERIAL_TYPE_BY_TAB = {
  converters: "CONVERTER",
  metals: "METAL",
  trucks: "TRUCK",
  boards: "BOARD",
};

function getOverrideKey(objectID) {
  return String(objectID);
}
function readArray(hit, key) {
  const v = (hit && (hit[key] ?? hit?.data?.[key])) ?? [];
  return Array.isArray(v) ? v : [];
}

/** Live pagination stats (nbHits, nbPages, etc.) */
function usePaginationInfo() {
  const stats = useConnector(connectStats, {});
  const page = useConnector(connectPagination, {});
  return {
    nbHits: stats?.nbHits ?? 0,
    hitsPerPage: stats?.hitsPerPage ?? 0,
    page: page?.currentRefinement ?? 1,
    nbPages: page?.nbPages ?? 0,
  };
}

/** Renders Pagination only when nbPages > 1; always mounted to keep hooks order stable */
function ConditionalPagination() {
  const { nbPages } = usePaginationInfo();
  if (nbPages <= 1) return null;
  return (
    <div className="mt-6 flex justify-center">
      <Pagination />
    </div>
  );
}

/** Scrolls to the top of results when the page changes */
function ScrollOnPageChange({ targetId = "conv-results-top" }) {
  const page = useConnector(connectPagination, {})?.currentRefinement ?? 1;
  const [initialPage] = React.useState(page);

  React.useEffect(() => {
    if (page === initialPage) return;
    const el = document.getElementById(targetId);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
    }
  }, [page, targetId, initialPage]);
  return null;
}

/** Shows the empty state when the server returns no hits (favorites mode) */
function ConditionalEmptyState({ hasAnyFavorites }) {
  const { nbHits } = usePaginationInfo();
  if (nbHits !== 0) return null;
  return (
    <NoFavorites
      isEmpty={true}
      message={
        hasAnyFavorites
          ? "No favorites match your search or filters."
          : "You haven't favorited anything yet."
      }
    />
  );
}

/**
 * BaseConverters - Unified component for Converters, Favorites, and Electronics pages
 * @param {Object} props
 * @param {'converters' | 'favorites' | 'electronics'} props.mode - The mode determines behavior:
 *   - 'converters': Normal browsing with index switcher (converters/metals/trucks/boards)
 *   - 'favorites': Shows only favorited items with facet filter
 *   - 'electronics': Shows only boards index (no index switcher)
 */
export default function BaseConverters({ mode = 'converters' }) {
  const { ready, settings, gates, pricing, uid } = useUser();
  const { theme: t, isForest } = useTheme();
  const { t: tr } = useTranslation();
  const searchClient = React.useMemo(() => createSafeSearchClient(appId, apiKey), []);
  const [pageSize] = React.useState(24);
  const [manageOpen, setManageOpen] = React.useState(false);

  const isFavoritesMode = mode === 'favorites';
  const isElectronicsMode = mode === 'electronics';

  // Use converters by default
  const defaultIndex = React.useMemo(() => IDX.converters, []);
  const isFreeRole = React.useMemo(() => String(settings?.discount || 'free').toLowerCase() === 'free', [settings?.discount]);

  // Index switching (metals/trucks/boards if permitted) - only for converters and favorites modes
  const [active, setActive] = React.useState(isElectronicsMode ? "boards" : "converters");
  const indexName = React.useMemo(() => {
    if (isElectronicsMode) return IDX.boards;
    if (active === "metals") return IDX.metals;
    if (active === "trucks") return IDX.trucks;
    if (active === "boards") return IDX.boards;
    return defaultIndex;
  }, [active, defaultIndex, isElectronicsMode]);

  const canMetals = !!gates?.navigation?.metal;
  const canTrucks = !!gates?.navigation?.truck;
  const canBoards = !!gates?.navigation?.boards && !!IDX.boards;

  // For favorites mode: check if user has any favorites
  const selectedListName = settings?.selectedList || "default";
  const favoriteIDs = React.useMemo(() => {
    const lists = settings?.lists || {};
    return Array.isArray(lists[selectedListName]) ? lists[selectedListName] : [];
  }, [settings?.lists, selectedListName]);
  const hasAnyFavorites = favoriteIDs && favoriteIDs.length > 0;

  // Details UI
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedHit, setSelectedHit] = React.useState(null);
  const openDetails = React.useCallback((hit) => {
    setSelectedHit(hit);
    setDetailsOpen(true);
  }, []);
  const closeDetails = React.useCallback(() => {
    setSelectedHit(null);
    setDetailsOpen(false);
  }, []);

  // Ensure boards lists schema exists when switching to Boards tab or in electronics mode
  React.useEffect(() => {
    if (!uid || (active !== 'boards' && !isElectronicsMode)) return;
    (async () => {
      try {
        const userRef = doc(db, 'users', uid);
        const snap = await getDoc(userRef);
        const d = snap.exists() ? (snap.data() || {}) : {};
        let listsB = d.listsBoards || {};
        let selB = d.selectedListBoard || 'default';
        let changed = false;
        if (!listsB || Object.keys(listsB).length === 0) {
          listsB = { default: { listName: 'default', lastUpdated: Date.now() } };
          selB = 'default';
          changed = true;
        }
        if (!d.selectedListBoard) { selB = 'default'; changed = true; }
        if (changed) {
          await setDoc(userRef, { selectedListBoard: selB, listsBoards: listsB }, { merge: true });
        }
      } catch (e) {
        console.warn('[BaseConverters] ensure boards lists failed', e);
      }
    })();
  }, [uid, active, isElectronicsMode]);

  // Compute final price in the user's selected currency
  const currency = settings?.currency || "USD";
  const computeFinalLocal = React.useCallback(
    (hit) => {
      const baseUSD = getBaseUSD(hit);
      const baseLocal = getPriceForCurrency(hit, currency);
      return applyLocalDiscount(baseLocal, baseUSD, pricing);
    },
    [pricing, currency]
  );

  // ----- Favorite wiring (CF: updateFavorite) -----
  const functions = React.useMemo(() => getFunctions(getApp(), "us-central1"), []);
  // updateFavorite2 handles all list types including BOARD (routes to publicBoards + boards index)
  const callUpdateFavorite = React.useMemo(() => httpsCallable(functions, "updateFavorite2"), [functions]);
  const callUpdateFavoriteBoards = callUpdateFavorite;

  // Server state reader from hit (robust to top-level or nested)
  const isFavoritedByMeFromHit = React.useCallback(
    (hit) => {
      if (!uid) return false;
      const arr = readArray(hit, "favorited_by");
      return arr.includes(uid);
    },
    [uid]
  );

  // -------- Persistent optimistic overrides (sessionStorage per uid) --------
  const overridesRef = React.useRef({});
  const [, setFavOverride] = React.useState({});
  const [favBusy, setFavBusy] = React.useState(() => new Set());
  const storageKey = uid ? `favOverrides:${uid}` : null;

  const loadOverrides = React.useCallback(() => {
    if (!storageKey) return {};
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  }, [storageKey]);

  const persistOverrides = React.useCallback(
    (obj) => {
      if (!storageKey) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(obj || {}));
      } catch {
        /* ignore */
      }
    },
    [storageKey]
  );

  React.useEffect(() => {
    if (!uid) {
      overridesRef.current = {};
      setFavOverride({});
      return;
    }
    const loaded = loadOverrides();
    overridesRef.current = loaded;
    setFavOverride(loaded);
  }, [uid, loadOverrides]);

  const computeIsFavorited = React.useCallback(
    (hit) => {
      // In favorites mode, all items shown are favorited
      if (isFavoritesMode) return true;
      const objectID = hit.objectID || hit.objectId || hit.id;
      const key = getOverrideKey(objectID);
      if (key in overridesRef.current) return !!overridesRef.current[key];
      return isFavoritedByMeFromHit(hit);
    },
    [isFavoritedByMeFromHit, isFavoritesMode]
  );

  const setOverride = React.useCallback(
    (objectID, value) => {
      const key = getOverrideKey(objectID);
      overridesRef.current = { ...overridesRef.current, [key]: !!value };
      setFavOverride(overridesRef.current);
      persistOverrides(overridesRef.current);
    },
    [persistOverrides]
  );

  const handleFavoriteClick = React.useCallback(
    async (hit) => {
      if (!uid) {
        console.warn("[favorite] blocked: no uid — sign in required");
        return;
      }
      const objectID = hit.objectID || hit.objectId || hit.id;
      if (!objectID) {
        console.warn("[favorite] blocked: missing objectID on hit", hit);
        return;
      }

      setFavBusy((prev) => {
        if (prev.has(objectID)) {
          console.log("[favorite] click ignored (busy)", objectID);
          return prev;
        }
        return new Set(prev).add(objectID);
      });

      const isBoardMode = active === 'boards' || isElectronicsMode;
      const listType = MATERIAL_TYPE_BY_TAB[active] || MATERIAL_TYPE_BY_TAB.converters;
      const current = computeIsFavorited(hit);

      // optimistic (only in non-favorites mode)
      if (!isFavoritesMode) {
        setOverride(objectID, !current);
      }

      try {
        const payload = { data: { objectID, uid, listType } };
        if (isBoardMode) {
          await callUpdateFavoriteBoards(payload);
        } else {
          await callUpdateFavorite(payload);
        }
      } catch (err) {
        if (!isFavoritesMode) {
          setOverride(objectID, current);
        }
        console.error("[favorite] updateFavorite failed:", err);
      } finally {
        setFavBusy((prev) => {
          const next = new Set(prev);
          next.delete(objectID);
          return next;
        });
      }
    },
    [uid, active, isElectronicsMode, isFavoritesMode, callUpdateFavorite, callUpdateFavoriteBoards, computeIsFavorited, setOverride]
  );

  // Add to Buy List handler
  const handleAddToCart = React.useCallback(async (hit, qty = 1) => {
    try {
      if (!uid) {
        console.warn("[buylist] blocked: no uid — sign in required");
        return;
      }
      const isBoardMode = active === 'boards' || isElectronicsMode;
      if (isBoardMode) {
        await addUserBuyListItemBoard({ uid, hit, deltaQty: Number(qty) || 1 });
      } else {
        await addUserBuyListItem({ uid, hit, deltaQty: Number(qty) || 1 });
      }
    } catch (err) {
      console.error("[buylist] add failed", err);
    }
  }, [uid, active, isElectronicsMode]);

  // Read inbound search params to seed UI state (query + brand refinements)
  const [searchParams] = useSearchParams();
  const inboundQuery = React.useMemo(() => (searchParams.get("q") || ""), [searchParams]);
  const inboundBrands = React.useMemo(() => {
    const list = searchParams.getAll("brand");
    if (list && list.length) return list;
    const csv = searchParams.get("brands");
    return csv ? csv.split(",").map((s) => s.trim()).filter(Boolean) : [];
  }, [searchParams]);

  const initialUiState = React.useMemo(() => ({
    [indexName]: {
      ...(inboundQuery ? { query: inboundQuery } : {}),
      ...(inboundBrands.length ? { refinementList: { "data.brand": inboundBrands } } : {}),
    },
  }), [indexName, inboundQuery, inboundBrands]);

  // Theme-based styles
  const buttonStyle = {
    backgroundColor: isForest ? t.surface.card : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
    color: isForest ? t.text.primary : t.text.primary,
  };

  const searchBoxClasses = {
    root: "flex-1",
    form: `w-full max-w-full rounded-2xl ring-1 ${isForest ? 'ring-white/10 bg-[#1a4d40]/60' : 'ring-green-200 bg-white shadow-sm'}`,
    input: `bg-transparent ${isForest ? 'text-green-50 placeholder:text-green-200/60' : 'text-green-900 placeholder:text-green-600/60'}`,
    submit: isForest ? "text-green-200" : "text-green-600",
    reset: isForest ? "text-green-200" : "text-green-600",
  };

  const deniedShadowColor = isForest ? 'rgba(0,0,0,0.55)' : (t?.shadow?.lg || 'rgba(76,175,80,0.2)');
  const deniedCardStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.9)' : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.15)' : t.surface.cardBorder}`,
    color: (t.cardText?.primary) || t.text.primary,
    boxShadow: `0 20px 60px ${deniedShadowColor}`,
  };
  const deniedTitleStyle = { color: t.cardText?.primary || t.text.primary };
  const deniedBodyStyle = {
    color: isForest ? (t.cardText?.secondary || t.text.secondary) : (t.text?.secondary || t.cardText?.secondary || t.text.primary),
  };
  const deniedIconWrapperStyle = {
    backgroundColor: isForest ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.12)',
    color: t.status?.error || '#ef4444',
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.2)'}`,
  };

  // Loading state
  if (!ready) return <div className="p-6" style={{ color: isForest ? t.text.tertiary : t.text.tertiary }}>Loading…</div>;

  // Access denied for free role (converters and electronics modes)
  if (isFreeRole && !isFavoritesMode) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-2xl p-6 text-center" style={deniedCardStyle}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={deniedIconWrapperStyle}>
            <ShieldAlert size={28} aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-semibold mb-3" style={deniedTitleStyle}>Access Denied.</h2>
          <p className="text-sm" style={deniedBodyStyle}>
            Access is limited to users with an active membership status.
          </p>
        </div>
      </div>
    );
  }

  // Favorites-specific checks
  if (isFavoritesMode) {
    if (isFreeRole) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-6">
          <div className="max-w-lg w-full rounded-2xl p-6 text-center" style={deniedCardStyle}>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={deniedIconWrapperStyle}>
              <ShieldAlert size={28} aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-semibold mb-3" style={deniedTitleStyle}>Access Denied.</h2>
            <p className="text-sm" style={deniedBodyStyle}>Access is limited to users with an active membership status.</p>
          </div>
        </div>
      );
    }
    if (!uid) {
      return (
        <div className="p-6" style={{ color: t.status?.error || '#ef4444' }}>
          You must be signed in to access Favorites.
        </div>
      );
    }
    // Note: We don't check hasAnyFavorites here anymore - let Algolia search run
    // and use ConditionalEmptyState to show empty message based on actual search results
  }

  const isBoardMode = active === 'boards' || isElectronicsMode;
  const resultsTargetId = isFavoritesMode ? "fav-results-top" : "conv-results-top";

  return (
    <ScreenGuard>
      <div className="px-0 md:px-0 pt-[2px] overflow-x-hidden" style={{ color: isForest ? t.text.primary : t.text.primary }}>
        <InstantSearch searchClient={searchClient} indexName={indexName} initialUiState={initialUiState}>
          <div className="flex flex-col gap-3">
            {/* Selected list + manage (above search, right-aligned) — mobile only */}
            <div className="md:hidden flex items-center justify-end gap-2">
              <ListSelector boardMode={isBoardMode} />
              <button
                onClick={() => setManageOpen(true)}
                className="px-2 py-1 rounded-lg transition-colors"
                style={buttonStyle}
                title="Manage lists"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Search */}
            <div className="flex items-center gap-2">
              <SearchBox
                placeholder={tr('home.searchPlaceholder')}
                classNames={searchBoxClasses}
                searchAsYouType={false}
              />
            </div>

            {/* Tabs + Selected List on desktop (right-aligned) - hide index switcher for electronics mode */}
            <div className="flex items-center justify-between gap-2">
              {!isElectronicsMode ? (
                <IndexSwitcher
                  current={active}
                  onChange={setActive}
                  canMetals={canMetals}
                  canTrucks={canTrucks}
                  canBoards={canBoards}
                />
              ) : (
                <div /> // Empty placeholder to maintain layout
              )}
              <div className="hidden md:flex items-center gap-2">
                <ListSelector boardMode={isBoardMode} />
                <button
                  onClick={() => setManageOpen(true)}
                  className="px-2 py-1 rounded-lg transition-colors"
                  style={buttonStyle}
                  title="Manage lists"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Configure: hitsPerPage + favorites filter */}
            <Configure
              hitsPerPage={pageSize}
              attributesToRetrieve={["*", "data.favorited_by"]}
              {...(isFavoritesMode ? { facetFilters: [[`data.favorited_by:${uid}`]] } : {})}
            />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Left: Filters */}
              <div className="md:col-span-3">
                {isBoardMode ? (<BoardsFiltersPanel />) : (<FiltersPanel indexName={indexName} searchClient={searchClient} />)}
              </div>

              {/* Right: Results */}
              <div className="md:col-span-9 converters-grid">
                <div id={resultsTargetId} />
                <Hits
                  classNames={{
                    list:
                      "grid grid-cols-1 md:grid-cols-2 gap-6 md:[grid-template-columns:repeat(2,minmax(0,1fr))] auto-rows-fr",
                    item:
                      "p-0 m-0 bg-transparent shadow-none border-0 rounded-none w-full h-full",
                  }}
                  hitComponent={({ hit }) => {
                    const finalLocal = computeFinalLocal(hit);
                    const isFav = computeIsFavorited(hit);
                    const objectID = hit.objectID || hit.objectId || hit.id;
                    const isBusy = favBusy.has(objectID);

                    return (
                      <div className="h-full w-full">
                        <ConverterCardLite
                          hit={hit}
                          computedPrice={isFreeRole ? undefined : finalLocal}
                          currencyCode={currency}
                          showPrice={!isFreeRole}
                          isFavorited={isFav}
                          favoriteDisabled={isBusy}
                          onFavoriteClick={(e) => {
                            e?.stopPropagation?.();
                            handleFavoriteClick(hit);
                          }}
                          onAddToCart={(e, qty) => {
                            e?.stopPropagation?.();
                            handleAddToCart(hit, qty);
                          }}
                          onImageClick={() => openDetails(hit)}
                          boardMode={isBoardMode}
                        />
                      </div>
                    );
                  }}
                />

                {/* Empty-state for favorites mode */}
                {isFavoritesMode && <ConditionalEmptyState hasAnyFavorites={hasAnyFavorites} />}

                {/* Pagination (conditionally rendered, hook used inside child) */}
                <ConditionalPagination />
                {/* Always-mounted scroll handler for page changes */}
                <ScrollOnPageChange targetId={resultsTargetId} />
              </div>
            </div>

            <DetailsSheetLite
              open={detailsOpen}
              onClose={closeDetails}
              hit={selectedHit}
              boardMode={isBoardMode}
              showWeight={!!gates?.details?.weight}
            />

            <ManageListsModal open={manageOpen} onClose={() => setManageOpen(false)} />
          </div>
        </InstantSearch>
      </div>
    </ScreenGuard>
  );
}
